/**
 * Request a reference letter — the form supervisors fill in, in People Space instead of Airtable
 * (David, Oct 12 2026). Every colleague sees it; they can pick only people in their reporting line
 * (the BP group: anyone), and the proxy re-checks that on submission. `?employee=<Personio id>`
 * pre-selects someone — the link in Boris's Slack reminder to a supervisor.
 *
 * Not asked any more: the employee's details and the supervisor (Personio and the login), and the
 * template (derived by the proxy from the answers and the Personio contract).
 *
 * Someone with an earlier request of the chosen type (often a partial one) gets it prefilled, and
 * Send updates that request rather than filing a second one (David, Oct 13 2026).
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { Loader2 } from 'lucide-react';
import { FormSubmissionError, fetchFormContext, submitReferenceLetterRequest } from '../services/referenceLetterFormApi';
import type { EarlierRequest, FormContext } from '../types/referenceLetterForm';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { ChoiceSelect, FieldRow, FormSection } from './referenceLetterRequest/RequestFields';
import { PersonCombobox } from './referenceLetterRequest/PersonCombobox';
import { GradeQuestion } from './referenceLetterRequest/GradeQuestion';
import { useGradeWording } from './referenceLetterRequest/useGradeWording';
import { useEarlierRequest } from './referenceLetterRequest/useEarlierRequest';
import { templateLabel } from './referenceLetters/templateLabels';
import { formatDate } from './referenceLetters/format';
import { MID_TERM, candidatesFor, emptyAnswers, formProgress, gradeQuestions, letterTypeFor, templateReady, toSubmission, withEarlier, type Answers } from './referenceLetterRequest/requestForm';

// The answers a followed link can change.
const LINK_KEYS = ['employeeId', 'type', 'exitReason', 'midTermReason'];

/** The form page: loads whom the caller may ask for, collects the answers, files the request. */
export default function ReferenceLetterRequestView() {
  const [params] = useSearchParams();
  const prefilled = params.get('employee');
  const [ctx, setCtx] = useState<FormContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>(() => emptyAnswers(prefilled));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ name: string; updated: boolean } | null>(null);
  const [sending, setSending] = useState(false);
  // Which link the loaded context was resolved for. A new ?employee= followed on the page is not
  // applied until its context loads; until then Send is off, or it would file for the old person.
  const [resolvedFor, setResolvedFor] = useState<string | null | undefined>(undefined);
  const linkPending = resolvedFor !== prefilled;
  // A ref, not state: two clicks in one tick must not both get past the check.
  const inFlight = useRef(false);

  useEffect(() => {
    void logAnalyticsEvent('reference_letter_request_viewed', { prefilled: Boolean(prefilled) });
    let current = true;
    fetchFormContext()
      .then((c) => {
        if (!current) return;
        setCtx(c);
        setResolvedFor(prefilled);
        // The link's person whenever the link changes (a second reminder followed on this page),
        // else whoever is chosen; someone the caller cannot reach is ignored, not sent along. A
        // link also sets the type of letter that person is listed under — a leaver's reminder
        // asks for an end-of-employment letter — or the picker would not offer them.
        // Whoever ends up chosen must be listed under the type, or the field would show nobody
        // while Send files for them.
        setAnswers((a) => {
          const wanted = c.people.find((p) => p.id === (prefilled ?? a.employeeId));
          if (!wanted) return { ...a, employeeId: null };
          const type = prefilled ? letterTypeFor(wanted, c.options.type) ?? a.type : a.type;
          const listed = candidatesFor(c.people, type).some((p) => p.id === wanted.id);
          return { ...a, employeeId: listed ? wanted.id : null, type, reason: type === a.type ? a.reason : null };
        });
        // Those answers may just have changed: a problem reported against them no longer applies.
        setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !LINK_KEYS.includes(k))));
      })
      .catch((err: Error) => current && setLoadError(err.message));
    return () => {
      current = false;
    };
  }, [prefilled]);

  const person = ctx?.people.find((p) => p.id === answers.employeeId) ?? null;
  const { wording, failed: wordingFailed } = useGradeWording(answers);
  const earlier = useEarlierRequest(answers.employeeId, answers.type, (found, previous) => {
    setAnswers((a) => withEarlier(a, found, previous));
    // What was reported against the old answers no longer applies to the prefilled ones.
    if (found || previous) setErrors({});
  });
  /** A new type of letter: its own reason, and the person only if that type still lists them. */
  const setType = (type: string) => {
    const keep = candidatesFor(ctx?.people ?? [], type).some((p) => p.id === answers.employeeId);
    set({ type, reason: null, employeeId: keep ? answers.employeeId : null });
  };
  /** Changes answers and clears the problems reported for exactly those answers. */
  const set = (patch: Partial<Answers>) => {
    setAnswers((a) => ({ ...a, ...patch }));
    const cleared = Object.keys(patch).flatMap((k) => (k === 'reason' ? ['exitReason', 'midTermReason'] : [k]));
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !cleared.includes(k))));
  };
  /** Sets one grade and clears the problem reported for it. */
  const setGrade = (key: string, value: string) => {
    setAnswers((a) => ({ ...a, grades: { ...a.grades, [key]: value } }));
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => k !== `grades.${key}`)));
  };

  /** Sends once; the proxy's per-field problems land under their fields. */
  const submit = async () => {
    if (inFlight.current || !ctx || linkPending || earlier.pending) return;
    if (!answers.employeeId) {
      setErrors({ employeeId: 'Choose who the letter is for' });
      setSendError(null);
      return;
    }
    inFlight.current = true;
    setSending(true);
    setErrors({});
    setSendError(null);
    try {
      // Only a partial request is updated; next to a complete one (a letter may already be out) a new one is filed.
      const shown = earlier.earlier;
      const updates = shown && !shown.complete ? { id: shown.id, version: shown.version } : null;
      const { updated } = await submitReferenceLetterRequest(toSubmission(answers, updates));
      setSent({ name: person?.name || 'this person', updated: Boolean(updated) });
      void logAnalyticsEvent('reference_letter_request_submitted', { type: answers.type ?? '', language: answers.language ?? '', scope: ctx.scope, updated: Boolean(updated) });
    } catch (err) {
      if (err instanceof FormSubmissionError) setErrors(err.details);
      setSendError(err instanceof Error ? err.message : 'The request could not be sent');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  if (loadError) return <Page><Banner variant="alert" description={loadError} /></Page>;
  if (!ctx) {
    return (
      <Page>
        <p className="flex items-center gap-2 text-sm text-zinc-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading the form…</p>
      </Page>
    );
  }
  if (sent) {
    return (
      <Page>
        <Banner
          variant="success"
          description={sent.updated
            ? `Request for ${sent.name} updated. The People team drafts the letter from it.`
            : `Request sent for ${sent.name}. The People team drafts the letter from it.`}
        />
        <div>
          <Button type="button" variant="primary" size="md" className="rounded" onClick={() => { setSent(null); earlier.forget(); setAnswers(emptyAnswers()); }}>
            Request another letter
          </Button>
        </div>
      </Page>
    );
  }
  if (ctx.people.length === 0) {
    return (
      <Page>
        <Banner variant="neutral" description="Personio shows nobody in your reporting line, so there is no one to request a letter for. If that is wrong, contact the People team." />
      </Page>
    );
  }

  const interim = answers.type === MID_TERM;
  const progress = formProgress(answers);
  // Steps 3 and 4 wait for the answers that choose the template, and say so.
  const locked = templateReady(answers)
    ? undefined
    : 'Choose the type of letter, the employee, the language and whether they lead people first: the sentences in the letter depend on them.';
  // A problem the proxy reported in a locked step is held back until it can be fixed there: inside
  // the dimmed step it would be unreadable and unanswerable, and the lock line says what to do.
  const unlockedError = (key: string) => (locked ? undefined : errors[key]);
  return (
    <Page>
      {/* Not a <form>: inside one, the DS Select's options were hidden from assistive tech (the
          whole form went aria-hidden while a dropdown was open), and Enter in the task notes must
          not send the request. */}
      <div className="flex flex-col gap-4">
        <FormSection step={1} title="Who is the letter for?">
          {/* The type first: it decides who is listed — leavers for a final letter, colleagues
              still employed for an interim one. */}
          <FieldRow id="type" label="Type of letter" error={errors.type}>
            <ChoiceSelect errorId="type-error" label="Type of letter" value={answers.type} options={ctx.options.type} error={errors.type} onChange={setType} />
          </FieldRow>
          <PersonCombobox
            people={candidatesFor(ctx.people, answers.type)}
            value={answers.employeeId}
            error={errors.employeeId}
            othersLabel={ctx.scope === 'all' ? 'Everyone else' : 'The rest of your reporting line'}
            disabledHint={answers.type ? undefined : 'Choose the type of letter first.'}
            emptyHint={interim ? 'Nobody you can request for is still employed.' : 'Nobody you can request for is leaving or has left.'}
            onChange={(id) => set({ employeeId: id })}
          />
          {answers.type && (
            <p className="text-sm text-zinc-700">
              {interim ? 'Colleagues still employed' : 'Colleagues who are leaving, or left in the last 12 months'}
              {ctx.scope === 'all' ? ', anyone at FINN.' : ', in your reporting line.'}
              {' '}Someone with two contracts (an internship, then a working-student job) is listed once per contract.
            </p>
          )}
          <EarlierNote earlier={earlier.earlier} failed={earlier.failed} />
        </FormSection>

        <FormSection step={2} title="About the letter">
          <FieldRow id="language" label="Language" error={errors.language}>
            <ChoiceSelect errorId="language-error" label="Language" value={answers.language} options={ctx.options.language} error={errors.language} onChange={(v) => set({ language: v })} />
          </FieldRow>
          {answers.type && (
            <FieldRow id="reason" label={interim ? 'Reason for the interim letter' : 'Reason for leaving'} error={errors[interim ? 'midTermReason' : 'exitReason']}>
              <ChoiceSelect
                errorId="reason-error"
                label={interim ? 'Reason for the interim letter' : 'Reason for leaving'}
                value={answers.reason}
                options={interim ? ctx.options.midTermReason : ctx.options.exitReason}
                error={errors[interim ? 'midTermReason' : 'exitReason']}
                onChange={(v) => set({ reason: v })}
              />
            </FieldRow>
          )}
          <FieldRow id="isLeader" label="Does the employee lead people?" error={errors.isLeader}>
            <ChoiceSelect errorId="isLeader-error" label="Does the employee lead people?" value={answers.isLeader} options={ctx.options.yesNo} error={errors.isLeader} onChange={(v) => set({ isLeader: v })} />
          </FieldRow>
        </FormSection>

        <FormSection step={3} title="Grades" locked={locked}>
          {!locked && (
            <p className="text-sm text-zinc-700">
              {wording
                ? `Under the grade you pick is the sentence it puts in the letter (${templateLabel(wording.template)}). The form of address is settled when the letter is drafted.`
                : wordingFailed
                  ? 'The sentences for each grade could not be loaded just now. You can still choose the grades.'
                  : 'Loading the sentences each grade puts in the letter…'}
            </p>
          )}
          {gradeQuestions(answers.isLeader).map((q) => (
            <GradeQuestion
              key={q.key}
              id={`grade-${q.key}`}
              label={q.label}
              hint={q.hint}
              grades={ctx.options.grade}
              value={answers.grades[q.key] ?? null}
              error={unlockedError(`grades.${q.key}`)}
              wording={wording?.grades[q.key] ?? null}
              fixed={Boolean(wording?.fixed.includes(q.key))}
              onChange={(v) => setGrade(q.key, v)}
            />
          ))}
        </FormSection>

        <FormSection step={4} title="Tasks" locked={locked}>
          <Textarea
            label="The employee's main tasks, one per line"
            rows={6}
            value={answers.tasks}
            onChange={(e) => set({ tasks: e.target.value })}
            error={unlockedError('tasks')}
          />
          <p className="text-sm text-zinc-700">Describe what they did, not how well: the grades above set the judgement in the letter.</p>
        </FormSection>

        {/* Pinned to the bottom of the page: how much is answered, and Send, always in reach. */}
        <div data-testid="send-bar" className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-x-0 border-b-0 border-t border-solid border-pearl bg-white px-4 py-3">
          {sendError && <Banner variant="alert" description={sendError} />}
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Not a live region: announcing the count after every answer is noise. */}
            <p className="text-sm text-zinc-700">
              <span className="font-semibold text-zinc-900">{progress.done} of {progress.total}</span> answered
            </p>
            <Button type="button" variant="primary" size="md" className="rounded" disabled={sending || linkPending || earlier.pending} onClick={() => void submit()}>
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} Send request
            </Button>
          </div>
        </div>
      </div>
    </Page>
  );
}

/**
 * Says where prefilled answers came from and what Send will do with them. A failed check is said
 * too: if a request exists after all, the proxy refuses the send and says so.
 */
function EarlierNote({ earlier, failed }: { earlier: EarlierRequest | null; failed: boolean }) {
  if (failed) {
    return <Banner variant="neutral" description="Earlier requests for this person could not be checked just now. If there is one, sending will say so." />;
  }
  if (!earlier) return null;
  const by = earlier.submittedBy;
  const on = earlier.created ? ` on ${formatDate(earlier.created)}` : '';
  const description = earlier.complete
    ? `${by ? `${by} already sent a complete request for this letter${on}.` : `A complete request for this letter was already sent${on}.`} Its answers are shown below. Sending files a new request; the earlier one is kept.`
    : `${by ? `${by} already started a request for this letter${on}` : `A request for this letter was already started${on}`}, but it is not complete. Its answers are shown below: fill in what is missing. Sending updates that request rather than filing a new one.`;
  return <Banner variant="neutral" description={description} />;
}

/** The page frame: title and the one line about what happens next. */
function Page({ children }: { children: React.ReactNode }) {
  return (
    // scroll-pb: a field focused by keyboard is scrolled clear of the pinned send bar, not under it.
    <div className="h-full scroll-pb-32 overflow-auto">
      {/* No bottom padding: the send bar is pinned there. */}
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 pt-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">✉️ Request a reference letter</h1>
          <p className="mt-1 text-sm text-zinc-700">
            Tell us what the employee did and how you rate them. Boris drafts the letter from your answers and the People
            team checks and sends it.
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}
